import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { API_BASE_URL } from 'nera-common/angular';
import { AuthService } from 'app/core/services/auth.service';
import { catchError, switchMap, throwError } from 'rxjs';
import { AlbumAccessService } from 'app/core/services/album-access.service';
import {
    clearGrantSession,
    readGrantSession,
} from 'app/core/services/auth/grant-session';

/** `GET p/{code}` and `POST p/{code}/session` — anonymous, never renewed. */
const GRANT_ENTRY_PATH = /\/api\/v\d+\/p\//;

/**
 * Keeps album-grant sessions out of the IAM login redirect.
 *
 * `neraAuthInterceptor` owns 401: it tries a BFF cookie refresh and, when that
 * fails (it always does for an anonymous grant principal), sends the browser
 * to IAM. That is the wrong outcome for a relative who opened `/p/{code}` —
 * their credential is the code, not an account. This interceptor sits INSIDE
 * the auth interceptor (registered after it), so on the way back up it sees a
 * 401 first: for a grant principal on an API URL it re-POSTs `p/{code}/session`,
 * retries the original request once with the fresh bearer, and the outer
 * interceptor only ever observes the retried outcome.
 *
 * When renewal itself fails (grant revoked / expired since the last visit),
 * the stored grant is dropped and the renewal error — a 403/404, never a 401
 * — is what surfaces, so the public gallery falls back to its
 * "invitation required" state instead of an IAM redirect.
 */
export const grantSessionInterceptor: HttpInterceptorFn = (req, next) => {
    const access = inject(AlbumAccessService);
    const auth = inject(AuthService);
    const apiBaseUrl = inject(API_BASE_URL);

    return next(req).pipe(
        catchError((error: unknown) => {
            if (
                !(error instanceof HttpErrorResponse) ||
                error.status !== 401 ||
                GRANT_ENTRY_PATH.test(req.url) ||
                !auth.user?.grant_id ||
                !(req.url.startsWith('/api/') || req.url.startsWith(apiBaseUrl))
            ) {
                return throwError(() => error);
            }
            const grant = readGrantSession();
            if (!grant) {
                return throwError(() => error);
            }
            return access.renewSession(grant.code).pipe(
                catchError((renewError: unknown) => {
                    clearGrantSession();
                    return throwError(() => renewError);
                }),
                switchMap((session) =>
                    next(
                        req.clone({
                            headers: req.headers.set(
                                'Authorization',
                                `Bearer ${session.accessToken}`
                            ),
                        })
                    )
                )
            );
        })
    );
};
